import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/integrations`;

export default function Integrations() {
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [configForm, setConfigForm] = useState({ api_key: '', api_secret: '', environment: 'sandbox' });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };
  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`${API}/providers`);
    if (res.ok) { const d = await res.json(); setProviders(d.providers || []); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfigure = async (code: string) => {
    const res = await authFetch(`${API}/${code}/configure`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(configForm) });
    if (res.ok) { showToast('Integration configured', true); setConfiguring(null); load(); }
    else showToast('Configuration failed', false);
  };

  const handleTest = async (code: string) => {
    const res = await authFetch(`${API}/${code}/test`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); showToast(`${d.status}: ${d.message}`, d.status === 'SUCCESS'); }
    else showToast('Test failed', false);
  };

  const handleSync = async (code: string) => {
    const res = await authFetch(`${API}/${code}/sync`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); showToast(d.message, true); load(); }
    else showToast('Sync failed', false);
  };

  const typeColors: Record<string, string> = { BANKING: '#3b82f6', PAYMENTS: '#8b5cf6', ERP: '#059669' };

  return (
    <PageLayout title="Integrations" subtitle="Connect to banking, payments, and ERP systems">
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: toast.ok ? '#22c55e' : '#ef4444', color: '#fff' }}>{toast.msg}</div>}

      {loading ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
          {providers.map(p => (
            <div key={p.provider_code} style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{p.provider_name}</div>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: typeColors[p.type] || '#64748b', color: '#fff', fontWeight: 600 }}>{p.type}</span>
                </div>
                <StatusBadge status={p.is_configured ? (p.is_active ? 'ACTIVE' : 'CONFIGURED') : 'NOT_CONFIGURED'} />
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 12 }}>
                Capabilities: {p.capabilities.join(', ')}
              </div>

              {p.last_sync && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 8 }}>Last sync: {p.last_sync}</div>}

              <div style={{ display: 'flex', gap: 6 }}>
                {!p.is_configured ? (
                  <ActionButton label="Configure" onClick={() => { setConfiguring(p.provider_code); setConfigForm({ api_key: '', api_secret: '', environment: 'sandbox' }); }} size="sm" />
                ) : (
                  <>
                    <ActionButton label="Test" onClick={() => handleTest(p.provider_code)} variant="secondary" size="sm" />
                    <ActionButton label="Sync" onClick={() => handleSync(p.provider_code)} variant="success" size="sm" />
                    <ActionButton label="Settings" onClick={() => { setConfiguring(p.provider_code); setConfigForm({ api_key: '', api_secret: '', environment: 'sandbox' }); }} variant="secondary" size="sm" />
                  </>
                )}
              </div>

              {configuring === p.provider_code && (
                <div style={{ marginTop: 12, padding: 12, background: 'var(--bg-hover)', borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <div><label style={{ fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 2 }}>API Key</label><input style={inputStyle} value={configForm.api_key} onChange={e => setConfigForm({ ...configForm, api_key: e.target.value })} type="password" /></div>
                    <div><label style={{ fontSize: 10, fontWeight: 600, display: 'block', marginBottom: 2 }}>API Secret</label><input style={inputStyle} value={configForm.api_secret} onChange={e => setConfigForm({ ...configForm, api_secret: e.target.value })} type="password" /></div>
                  </div>
                  <div style={{ marginBottom: 8 }}><label style={{ fontSize: 10, fontWeight: 600 }}>Environment</label><select style={{ ...inputStyle, marginTop: 2 }} value={configForm.environment} onChange={e => setConfigForm({ ...configForm, environment: e.target.value })}><option value="sandbox">Sandbox</option><option value="production">Production</option></select></div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <ActionButton label="Save" onClick={() => handleConfigure(p.provider_code)} size="sm" />
                    <ActionButton label="Cancel" onClick={() => setConfiguring(null)} variant="secondary" size="sm" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageLayout>
  );
}
